import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@wms/shared';
import { apiFetch } from '../../lib/api';
import { useCan } from '../../lib/session';
import { UomCreateModal } from './uom-create-modal';
import type { UomItem } from './types';

/** 单位下拉里"＋ 新建单位…"占位项的 value——不可能与真实 uuid 撞车 */
const CREATE_UOM_VALUE = '__create_uom__';

/**
 * 【用户三格式设计】箱（外包装）→ 支（基本单位）→ 24（每箱数量）＝ 一箱 24 支。
 * 从 product-create-page.tsx 抽出的共享字段组——0023 的入库强制单位补全面板
 * 需要复用同一套交互（含"＋ 新建单位"联动），而不是维护两份几乎相同的 JSX。
 */
export function PackagingUnitFields({
  baseUnitId,
  onBaseUnitIdChange,
  packagingUomId,
  onPackagingUomIdChange,
  packagingQty,
  onPackagingQtyChange,
}: {
  readonly baseUnitId: string;
  readonly onBaseUnitIdChange: (id: string) => void;
  readonly packagingUomId: string;
  readonly onPackagingUomIdChange: (id: string) => void;
  readonly packagingQty: string;
  readonly onPackagingQtyChange: (v: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [uomModalTarget, setUomModalTarget] = useState<'base' | 'packaging' | null>(null);
  // 单位字典归 settings.manage（uom.controller.ts）；没权限就不渲染入口
  const canManageSettings = useCan(PERMISSIONS.SETTINGS_MANAGE);

  const { data: uomsData } = useQuery({
    queryKey: ['uoms'],
    queryFn: () => apiFetch<{ items: UomItem[] }>('/uoms'),
  });
  const activeUoms = (uomsData?.items ?? []).filter((u) => !u.archivedAt);
  const codeOf = (id: string): string => activeUoms.find((u) => u.id === id)?.code ?? '';
  const qtyValid = /^[1-9]\d*$/.test(packagingQty.trim());

  return (
    <>
      <div className="row" style={{ gap: 'var(--space-3)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label className="field" style={{ flex: '1 1 150px' }}>
          <span>{t('products.packagingUnit')}</span>
          <select
            value={packagingUomId}
            onChange={(e) => {
              if (e.target.value === CREATE_UOM_VALUE) {
                setUomModalTarget('packaging');
                return; // 占位项不落入 state，选择框停在原值
              }
              onPackagingUomIdChange(e.target.value);
            }}
          >
            <option value="">{t('products.packagingNone')}</option>
            {activeUoms
              .filter((u) => u.id !== baseUnitId)
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.code}
                </option>
              ))}
            {canManageSettings && <option value={CREATE_UOM_VALUE}>{t('units.createOption')}</option>}
          </select>
        </label>
        <label className="field" style={{ flex: '1 1 150px' }}>
          <span>{t('products.baseUnit')}</span>
          <select
            value={baseUnitId}
            onChange={(e) => {
              if (e.target.value === CREATE_UOM_VALUE) {
                setUomModalTarget('base');
                return;
              }
              onBaseUnitIdChange(e.target.value);
            }}
            required
          >
            <option value="" disabled>
              {t('units.selectUnit')}
            </option>
            {activeUoms
              .filter((u) => u.id !== packagingUomId)
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.code}
                </option>
              ))}
            {canManageSettings && <option value={CREATE_UOM_VALUE}>{t('units.createOption')}</option>}
          </select>
        </label>
        {packagingUomId !== '' && (
          <label className="field" style={{ flex: '0 1 130px' }}>
            <span>{t('products.unitsPerPackaging')}</span>
            <input
              value={packagingQty}
              onChange={(e) => onPackagingQtyChange(e.target.value)}
              inputMode="numeric"
              pattern="[1-9][0-9]*"
              placeholder="24"
              required
            />
          </label>
        )}
      </div>
      <p className="field-hint">
        {packagingUomId && baseUnitId && qtyValid
          ? `1 ${codeOf(packagingUomId)} = ${packagingQty.trim()} ${codeOf(baseUnitId)}`
          : t('products.packagingHint')}
      </p>

      {/* 放在外层 form 之外——弹窗自身是 form，嵌套 form 是无效 HTML */}
      {uomModalTarget !== null && (
        <UomCreateModal
          onClose={() => setUomModalTarget(null)}
          onCreated={(id) => {
            // 新建即选中触发弹窗的那个下拉，用户不用再找一遍
            if (uomModalTarget === 'packaging') onPackagingUomIdChange(id);
            else onBaseUnitIdChange(id);
            setUomModalTarget(null);
          }}
        />
      )}
    </>
  );
}
